import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { LazyDataLoader } from '@/lib/utils/lazy-data-loader';
import { DBCollection } from '@/lib/utils/constants-shared';
import { useTestGuidedStudy, ENABLE_TEST_GUIDED_STUDY, TEST_PASSAGE_ID } from '@/lib/hooks/use-test-guided-study';

interface GuidedStudyByPassageDataContextType {
    guidedStudyByPassage: GuidedStudyByPassage[];
    guidedStudyByPassageMap: Map<string, GuidedStudyByPassage>;
    loadGuidedStudyByPassageId: (passageId: string) => Promise<GuidedStudyByPassage | undefined>;
    invalidateCache: (passageId: string) => Promise<void>;
}

export const GuidedStudyByPassageDataContext = createContext<GuidedStudyByPassageDataContextType | undefined>(undefined);

export function GuidedStudyByPassageDataProvider({
    children,
    guidedStudyByPassage: initialData
}: {
    children: React.ReactNode;
    guidedStudyByPassage: GuidedStudyByPassage[];
}) {
    const [loader] = useState(() => new LazyDataLoader<GuidedStudyByPassage>(DBCollection.guidedStudyByPassage, 'passageId'));
    const [cachedItems, setCachedItems] = useState<GuidedStudyByPassage[]>([]);

    useEffect(() => {
        if (initialData && initialData.length > 0) {
            loader.preload(initialData);
            setCachedItems(loader.getAllCached());
        }
    }, [initialData, loader]);

    const [guidedStudyByPassageMap, setGuidedStudyByPassageMap] = useState(() => {
        const map = new Map<string, GuidedStudyByPassage>();
        cachedItems.forEach(item => map.set(item.passageId, item));
        return map;
    });

    // Update map when cached items change - create NEW map to trigger React updates
    useEffect(() => {
        const newMap = new Map<string, GuidedStudyByPassage>();
        cachedItems.forEach(item => newMap.set(item.passageId, item));
        setGuidedStudyByPassageMap(newMap);
    }, [cachedItems]);

    const loadGuidedStudyByPassageId = useCallback(async (passageId: string): Promise<GuidedStudyByPassage | undefined> => {
        const item = await loader.get(passageId);
        if (item) {
            setCachedItems(loader.getAllCached());
        }
        return item;
    }, [loader]);

    const invalidateCache = useCallback(async (passageId: string): Promise<void> => {
        await loader.invalidate(passageId);
        setCachedItems(loader.getAllCached());
    }, [loader]);

    return (
        <GuidedStudyByPassageDataContext.Provider value={{
            guidedStudyByPassage: cachedItems,
            guidedStudyByPassageMap,
            loadGuidedStudyByPassageId,
            invalidateCache
        }}>
            {children}
        </GuidedStudyByPassageDataContext.Provider>
    );
}

export function useGuidedStudyByPassageData(passageId?: string) {
    const context = useContext(GuidedStudyByPassageDataContext);
    if (context === undefined) {
        throw new Error('useGuidedStudyByPassageData must be used within a GuidedStudyByPassageDataProvider');
    }

    const [data, setData] = useState<GuidedStudyByPassage | undefined>();
    const [isLoading, setIsLoading] = useState(false);

    // 🧪 TEMPORARY: Check if we should use test fixture
    const { testData, isLoading: testLoading, isTestMode } = useTestGuidedStudy(passageId || '', '');

    useEffect(() => {
        if (!passageId) {
            return;
        }

        // 🧪 TEMPORARY: Use test fixture if enabled
        if (ENABLE_TEST_GUIDED_STUDY && passageId === TEST_PASSAGE_ID && testData) {
            console.log('🧪 Using test fixture for guided study');
            setData(testData as any);
            setIsLoading(false);
            return;
        }

        const cached = context.guidedStudyByPassageMap.get(passageId);
        if (cached) {
            setData(cached);
            return;
        }

        setIsLoading(true);
        context.loadGuidedStudyByPassageId(passageId)
            .then(loaded => {
                setData(loaded);
                setIsLoading(false);
            })
            .catch(err => {
                console.error(`Failed to load guided study by passage for ${passageId}:`, err);
                setIsLoading(false);
            });
    }, [passageId, context, context.guidedStudyByPassageMap, testData]);

    return {
        data,
        isLoading: isTestMode ? testLoading : isLoading,
        ...context
    };
}

